/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Button, TruncatedText, Tooltip, Flyout, SVGIcon } from 'ca-ui-toolkit';
import { ShapingAncestorTree } from 'ca-modeller/shaping';
import closeIcon from '@ba-ui-toolkit/ba-graphics/dist/icons/close_32.svg';
import StringResources from 'nls/StringResources';
import PropTypes from 'util/PropTypes';
import classNames from 'classnames';
import './fieldButton.scss';

const BUTTON_TEXT_MAX_WIDTH = 200;
@observer
class FieldButton extends Component {

	constructor(props) {
		super(props);
		this.state = {
			isFlyoutShown: false,
			needResetTextMaxWidth: false
		};
		this.ref = React.createRef();

		this.textRef = React.createRef();

		this.dismissButtonRef = React.createRef();
	}

	render() {
		if (this.props.shouldUseDisambiguatedPaths) {
			return this._renderButtonWithDisambiguatedTooltip();
		}
		return this._renderButtonWithTooltip();
	}

	componentDidMount() {
		// for deleteable button, have to reset the max width for the text if needed
		if (this.props.deletable && this.textRef.current) {
			const initTextWidth = this.textRef.current._initTextWidth;
			if(initTextWidth && initTextWidth >= BUTTON_TEXT_MAX_WIDTH) {
				this.setState({
					needResetTextMaxWidth: true
				});
			}
		}
	}

	_renderButtonWithDisambiguatedTooltip() {
		const buttonLabel = this.props.label;
		return (
			<div>
				{ this._renderFlyout() }
				<Button
					variant="frame"
					intent="primary"
					onClick={(event) => {
						if (this.props.deletable) {
							if (this._handleDelete(event)){
							//if handled for delete, then return
								return;
							}
						}
						this.props.onClick && this.props.onClick();
					}}
					className={classNames({
						'fieldButton': true,
						'deletable-fieldButton': this.props.deletable
					})}
					id={this.props.id}
					onMouseEnter={this._showFlyout}
					onMouseLeave={this._hideFlyout}
					onKeyDown={this._onKeyDown}
					ref={this.ref}
				>
					<TruncatedText style={this.state.needResetTextMaxWidth ? { maxWidth: BUTTON_TEXT_MAX_WIDTH } : {}} ref={this.textRef} value={buttonLabel} allowTooltip={false}/>
					{
						this.props.deletable ? <div
							ref={this.dismissButtonRef}
							className="fieldButton__dismissIcon"
							aria-label={StringResources.get('fieldButton_delete')}
							title={StringResources.get('fieldButton_delete')}
							onClick={
								e => {
									this.props.onDelete && this.props.onDelete();
									e.stopPropagation();
								}
							}
						>
							<SVGIcon iconId={closeIcon.id} width={14} height={14}/>
						</div> : null
					}
				</Button>
			</div>
		);
	}

	_renderButtonWithTooltip() {
		const buttonLabel = this.props.label;
		const buttonTooltip = `${this.props.label} (${StringResources.get('tooltipId')}: ${this.props.id})`;
		return (
			<Tooltip
				title={buttonTooltip}
				orient="top"
			>
				<Button
					variant="frame"
					intent="primary"
					onClick={this.props.onClick}
					className="fieldButton"
					id={this.props.id}
				>
					<TruncatedText value={buttonLabel} allowTooltip={false}/>
				</Button>
			</Tooltip>
		);
	}

	_onKeyDown = (e) => {
		switch (e.key) {
			case 'Backspace':
			case 'Delete':
				if(this.props.deletable) {
					this.props.onDelete && this.props.onDelete();
					e.stopPropagation();
				}
				break;
			default:
				break;
		}
	}

	_showFlyout = () => {
		this.setState({ isFlyoutShown: true });
	}

	_hideFlyout = () => {
		this.setState({ isFlyoutShown: false });
	}

	_shapingAncestorTree_includeParent = () => true

	_renderFlyout = () => {
		if (this.state.isFlyoutShown && this.ref.current) {
			const tooltipLabel = StringResources.get('relationshipGraph_source_tooltip_label_title');
			return (
				<Flyout
					domNodeToAttachTo={document.body}
					triggerNode={this.ref._node}
					offset={10}
					placement={'topCentred'}
					candidatePlacement={['topCentred']}
					onClose={this._hideFlyout}
				>
					<div className="relationship-vis-view__disambiguatingTooltipTitle">{tooltipLabel}</div>
					<div className="relationship-vis-view__disambiguatingTooltip">{this._renderQuerySubjectItem()}</div>
				</Flyout>
			);
		}
	}

	/**
	 * @return {boolean} true if handled, false if not
	 */
	_handleDelete(event) {
		const x = (event.hasOwnProperty('clientX')) ? event.clientX : null;
		const y = (event.hasOwnProperty('clientY')) ? event.clientY : null;
		if(x !== null && y !== null) {
			const dismissButtonRect = this.dismissButtonRef.current && this.dismissButtonRef.current.getBoundingClientRect();
			if (dismissButtonRect) {
				const { left, top, width, height } = dismissButtonRect;
				const xOffset = x - left;
				const yOffset = y - top;
				if(xOffset >= 0 && xOffset <= width && yOffset >= 0 && yOffset <= height) {
				// click is inside the dismiss button, handle deletemaster
					this.props.onDelete && this.props.onDelete();
					return true;
				}
			}
		}
		return false;
	}

	_renderQuerySubjectItem() {
		const { activeDataSourceModule, id } = this.props;
		const shapingHelper = activeDataSourceModule.value.getShapingHelper();
		const moserColumn = activeDataSourceModule.value.getMetadataColumn(id).moserObject;

		if (moserColumn) {
			return (
				<shapingHelper.ShapingProvider>
					<ShapingAncestorTree
						includeParent={this._shapingAncestorTree_includeParent}
						moserObject={moserColumn}
					/>
				</shapingHelper.ShapingProvider>
			);
		}
		return null;
	}
}
FieldButton.defaultProps = {
	onClick: () => {},
	deletable: false,
	onDelete: () => {},
	shouldUseDisambiguatedPaths: true
};
FieldButton.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	onClick: PropTypes.func,
	activeDataSourceModule: PropTypes.observableObject.isRequired,
	shouldUseDisambiguatedPaths: PropTypes.bool,
	deletable: PropTypes.bool,
	onDelete: PropTypes.func
};

export default FieldButton;



// WEBPACK FOOTER //
// ./src/js/components/fieldsList/FieldButton.js